<template lang="pug">
.ber-ratings(v-if="report" :style="{'--berwow-current-content': `'${$t('rating.current')}${currentRatingIsPotentialA0 ? '*' : ''}'`}")
  div(v-for="rating, key in ratings" :key="key" :class="ratingClasses(rating)")
    span(v-text="rating")
    span(:class="`ber-rating-${rating.toLowerCase()}`" :style="{backgroundColor: getRatingColor(rating), flexBasis: (30 + (50 / ratings.length) * key)+'px'}")
    span.new-label(v-if="report.afterRating.code === rating") {{ $t('rating.new') }}{{ newRatingIsPotentialA0 ? '*' : '' }}
    span.best-rating(v-if="displayCode(berRatings?.best.code) === rating")
      span(v-tooltip.left="$t('rating.bestInAreaTooltip')") {{ $t('rating.bestInArea') }}
    span.average-rating(v-if="displayCode(berRatings?.mean.code) === rating")
      span(v-tooltip.left="$t('rating.avgInAreaTooltip')") {{ $t('rating.avgInArea') }}
  .ber-rating-footnote(v-if="hasPotentialA0Rating" :title="potentialA0Tooltip") {{ $t('rating.potentialA0Footnote') }}
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { BerRatings, getDisplayRatingCode, getRatingColor } from "@/helpers";
import { storeToRefs } from "pinia";
import { useRecommendations } from "@/store/recommendations";
import { useBerRatings } from "@/store/map-ber-rating";
import { useSettings } from "@/store";
import { AvailableCountryISO3 } from "@/types";

export default defineComponent({
  name: "BerRatings",
  setup() {
    const ratings = [...BerRatings.keys()];
    const { settings } = storeToRefs(useSettings());
    const { report } = storeToRefs(useRecommendations());
    const { berRatings } = storeToRefs(useBerRatings());
    const isPotentialA0Display = (rating?: { code: string; isPotentialA0?: boolean }) =>
      !!rating?.isPotentialA0 && getDisplayRatingCode(rating.code) !== "A0";
    const currentRatingIsPotentialA0 = computed(() =>
      isPotentialA0Display(report.value?.beforeRating)
    );
    const newRatingIsPotentialA0 = computed(() =>
      isPotentialA0Display(report.value?.afterRating)
    );
    const hasPotentialA0Rating = computed(() =>
      currentRatingIsPotentialA0.value || newRatingIsPotentialA0.value
    );
    const potentialA0Tooltip = computed(() =>
      (isPotentialA0Display(report.value?.beforeRating)
        ? report.value?.beforeRating.annotationTooltip
        : undefined)
      ?? (isPotentialA0Display(report.value?.afterRating)
        ? report.value?.afterRating.annotationTooltip
        : undefined)
    );

    const ratingClasses = (rating: string) => {
      return report.value
        ? ["ber-rating", `${rating.toLowerCase()}`].concat(
            report.value.afterRating.code === rating ? ["new-flag ber-flag"] : [],
            report.value.beforeRating.code === rating ? ["current-flag ber-flag"] : []
          )
        : [];
    };

    return {
      ratings,
      ratingClasses,
      report,
      berRatings,
      currentRatingIsPotentialA0,
      newRatingIsPotentialA0,
      hasPotentialA0Rating,
      potentialA0Tooltip,
      settings,
      AvailableCountryISO3,
      getRatingColor,
      displayCode: getDisplayRatingCode,
    };
  },
});
</script>
<style lang="scss">
@import "@/assets/styles/components/ber-rating.scss";
</style>
